import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DocketAPIError, getAPIClient } from "../../../apiClient";
import { useAtom } from "jotai";
import { userAtom, devicesAtom, isHydrated } from "../../../jotai/atoms";
import { ImageIcon } from "../../../components/ImageIcon";
import { Capacitor } from "@capacitor/core";
import { Dialog } from "@capacitor/dialog";
import { registerPushPermission } from "../../../pushNotifications";
import { updateDevicesAtom } from "../../../jotai/atomUpdater";
import Bugsnag from "@bugsnag/js";

interface NotificationPreferencesCardProps {
  onError: (message: string) => void;
}

export function NotificationPreferencesCard({ onError }: NotificationPreferencesCardProps) {
  const { t } = useTranslation();
  const [user, setUser] = useAtom(userAtom);
  const [devices] = useAtom(devicesAtom);

  const [selectedDisableAll, setSelectedDisableAll] = useState<boolean>(false);
  const [selectedEmailReminders, setSelectedEmailReminders] = useState<boolean>(false);
  const [selectedEmailRecordUpdates, setSelectedEmailRecordUpdates] = useState<boolean>(false);
  const [selectedAppUpdates, setSelectedAppUpdates] = useState<boolean>(false);
  const [selectedPushRecordUpdates, setSelectedPushRecordUpdates] = useState<boolean>(false);
  const [selectedPushReminders, setSelectedPushReminders] = useState<boolean>(false);
  const [selectedPushAppUpdates, setSelectedPushAppUpdates] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const showPushNotifications = useMemo(() => {
    return Capacitor.isNativePlatform();
  }, []);

  useEffect(() => {
    setSelectedDisableAll(user?.notification_preferences?.disable_all || false);
    setSelectedEmailRecordUpdates(user?.notification_preferences?.email_new_results || false);
    setSelectedEmailReminders(user?.notification_preferences?.email_upcoming_vaccines || false);
    setSelectedAppUpdates(user?.notification_preferences?.email_app_updates || false);
    setSelectedPushRecordUpdates(user?.notification_preferences?.push_new_results || false);
    setSelectedPushReminders(user?.notification_preferences?.push_upcoming_vaccines || false);
    setSelectedPushAppUpdates(user?.notification_preferences?.push_app_updates || false);
  }, [user]);

  const notificationHasChanged = () => {
    return (
      selectedDisableAll !== user?.notification_preferences?.disable_all ||
      selectedEmailRecordUpdates !== user?.notification_preferences?.email_new_results ||
      selectedEmailReminders !== user?.notification_preferences?.email_upcoming_vaccines ||
      selectedAppUpdates !== user?.notification_preferences?.email_app_updates ||
      selectedPushRecordUpdates !== user?.notification_preferences?.push_new_results ||
      selectedPushReminders !== user?.notification_preferences?.push_upcoming_vaccines ||
      selectedPushAppUpdates !== user?.notification_preferences?.push_app_updates
    );
  };

  const handleSaveNotificationPreferences = async () => {
    const notification_preferences = {
      disable_all: selectedDisableAll,
      email_new_results: selectedDisableAll ? false : selectedEmailRecordUpdates,
      email_upcoming_vaccines: selectedDisableAll ? false : selectedEmailReminders,
      email_app_updates: selectedDisableAll ? false : selectedAppUpdates,
      push_new_results: selectedDisableAll ? false : selectedPushRecordUpdates,
      push_upcoming_vaccines: selectedDisableAll ? false : selectedPushReminders,
      push_app_updates: selectedDisableAll ? false : selectedPushAppUpdates,
    };

    setSaving(true);
    try {
      await getAPIClient().updateUser({ notification_preferences });
      const userResults = await getAPIClient().getUser();
      if (userResults) {
        await setUser(userResults);
      }

      setSaving(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 500);
    } catch (e: unknown) {
      setSaving(false);
      if (e instanceof DocketAPIError) {
        onError(e.message);
      }
    }
  };

  const handleDeleteDevice = async (deviceId: string) => {
    const device = isHydrated(devices)
      ? devices.find((device) => device.device_id === deviceId)
      : undefined;
    const { value } = await Dialog.confirm({
      title: t("settings.delete_device_title"),
      message: t("settings.delete_device_message", { name: device?.name }),
    });

    if (value) {
      try {
        await getAPIClient().deleteDevice(deviceId);
        await updateDevicesAtom();
      } catch (e: unknown) {
        if (e instanceof DocketAPIError) {
          onError(t("settings.delete_device_error"));
          Bugsnag.notify(e as Error);
        }
      }
    }
  };

  return (
    <div className="card bg-base-100 max-md:rounded-none">
      <div className="bg-secondary text-secondary-content px-4 py-3 rounded-t-[var(--radius-box)] max-md:rounded-none">
        <h2 className="text-xl font-bold">{t("settings.notif_prefs_header")}</h2>
      </div>
      <div className="card-body">
        <div className="flex flex-col gap-4">
          <label htmlFor="notif-disable-all" className="flex items-center gap-4 cursor-pointer">
            <input
              id="notif-disable-all"
              type="checkbox"
              className="toggle toggle-primary"
              checked={selectedDisableAll}
              onChange={(e) => {
                const checked = e.target.checked;
                setSelectedDisableAll(checked);
                if (checked) {
                  setSelectedEmailRecordUpdates(false);
                  setSelectedEmailReminders(false);
                  setSelectedAppUpdates(false);
                  setSelectedPushRecordUpdates(false);
                  setSelectedPushReminders(false);
                  setSelectedPushAppUpdates(false);
                }
              }}
            />
            <span>{t("settings.notif_prefs_disable")}</span>
          </label>
          <p className="text-sm italic">{t("settings.notif_disable_message")}</p>

          <p className="font-bold">{t("settings.notif_prefs_email")}</p>

          <label htmlFor="notif-email-records" className="flex items-center gap-4 cursor-pointer">
            <input
              id="notif-email-records"
              type="checkbox"
              className="toggle toggle-primary"
              disabled={selectedDisableAll}
              checked={selectedEmailRecordUpdates}
              onChange={(e) => setSelectedEmailRecordUpdates(e.target.checked)}
            />
            <span className={selectedDisableAll ? "text-gray-400" : ""}>
              {t("settings.notif_immunization_records_updates")}
            </span>
          </label>

          <label htmlFor="notif-email-reminders" className="flex items-center gap-4 cursor-pointer">
            <input
              id="notif-email-reminders"
              type="checkbox"
              className="toggle toggle-primary"
              disabled={selectedDisableAll}
              checked={selectedEmailReminders}
              onChange={(e) => setSelectedEmailReminders(e.target.checked)}
            />
            <span className={selectedDisableAll ? "text-gray-400" : ""}>
              {t("settings.notif_immunization_reminders")}
            </span>
          </label>

          <label
            htmlFor="notif-email-app-updates"
            className="flex items-center gap-4 cursor-pointer"
          >
            <input
              id="notif-email-app-updates"
              type="checkbox"
              className="toggle toggle-primary"
              disabled={selectedDisableAll}
              checked={selectedAppUpdates}
              onChange={(e) => setSelectedAppUpdates(e.target.checked)}
            />
            <span className={selectedDisableAll ? "text-gray-400" : ""}>
              {t("settings.notif_immunization_versions")}
            </span>
          </label>

          {showPushNotifications && (
            <>
              <p className="font-bold">{t("settings.notif_prefs_push")}</p>

              <label
                htmlFor="notif-push-records"
                className="flex items-center gap-4 cursor-pointer"
              >
                <input
                  id="notif-push-records"
                  type="checkbox"
                  className="toggle toggle-primary"
                  disabled={selectedDisableAll}
                  checked={selectedPushRecordUpdates}
                  onChange={(e) => setSelectedPushRecordUpdates(e.target.checked)}
                />
                <span className={selectedDisableAll ? "text-gray-400" : ""}>
                  {t("settings.notif_immunization_records_updates")}
                </span>
              </label>

              <label
                htmlFor="notif-push-reminders"
                className="flex items-center gap-4 cursor-pointer"
              >
                <input
                  id="notif-push-reminders"
                  type="checkbox"
                  className="toggle toggle-primary"
                  disabled={selectedDisableAll}
                  checked={selectedPushReminders}
                  onChange={(e) => setSelectedPushReminders(e.target.checked)}
                />
                <span className={selectedDisableAll ? "text-gray-400" : ""}>
                  {t("settings.notif_immunization_reminders")}
                </span>
              </label>

              <label
                htmlFor="notif-push-app-updates"
                className="flex items-center gap-4 cursor-pointer"
              >
                <input
                  id="notif-push-app-updates"
                  type="checkbox"
                  className="toggle toggle-primary"
                  disabled={selectedDisableAll}
                  checked={selectedPushAppUpdates}
                  onChange={(e) => setSelectedPushAppUpdates(e.target.checked)}
                />
                <span className={selectedDisableAll ? "text-gray-400" : ""}>
                  {t("settings.notif_immunization_versions")}
                </span>
              </label>
            </>
          )}

          {showPushNotifications && isHydrated(devices) && devices.length > 0 && (
            <>
              <p className="font-bold">{t("settings.notif_prefs_devices")}</p>
              <p className="text-sm italic">{t("settings.notif_prefs_devices_message")}</p>
              {devices.map((device) => (
                <label
                  key={device.device_id}
                  htmlFor={`notif-device-${device.device_id}`}
                  className="flex items-center gap-4 cursor-pointer"
                >
                  <input
                    id={`notif-device-${device.device_id}`}
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={true}
                    onChange={() => handleDeleteDevice(device.device_id)}
                  />
                  <span>{device.name}</span>
                </label>
              ))}
            </>
          )}

          <button
            className="btn btn-primary w-fit"
            onClick={handleSaveNotificationPreferences}
            disabled={!notificationHasChanged() || saving}
          >
            {saving ? (
              <span className="loading loading-spinner loading-sm"></span>
            ) : showSuccess ? (
              <ImageIcon src="/assets/images/Icon_Check.png" className="w-5 h-5" />
            ) : (
              t("settings.update_notifications")
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NotificationPreferencesCard;
